import React, { useEffect, useState } from 'react';
import { Pill, PillBottle, Plus, Edit3, Save, X, AlertTriangle, Ban, Check } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import PatientDropdown from '../components/PatientDropdown';

const ATC_LEVEL1_CLASSES = [
  { code: 'A', label: 'A — Alimentary tract and metabolism' },
  { code: 'B', label: 'B — Blood and blood forming organs' },
  { code: 'C', label: 'C — Cardiovascular system' },
  { code: 'D', label: 'D — Dermatologicals' },
  { code: 'G', label: 'G — Genito-urinary system and sex hormones' },
  { code: 'H', label: 'H — Systemic hormonal preparations' },
  { code: 'J', label: 'J — Anti-infectives for systemic use' },
  { code: 'L', label: 'L — Antineoplastic and immunomodulating agents' },
  { code: 'M', label: 'M — Musculo-skeletal system' },
  { code: 'N', label: 'N — Nervous system' },
  { code: 'P', label: 'P — Antiparasitic products' },
  { code: 'R', label: 'R — Respiratory system' },
  { code: 'S', label: 'S — Sensory organs' },
  { code: 'V', label: 'V — Various' },
];

const VA_DRUG_CLASSES = [
  { code: 'AM', label: 'AM — Antimicrobials' },
  { code: 'CV', label: 'CV — Cardiovascular' },
  { code: 'CN', label: 'CN — Central Nervous System' },
  { code: 'GA', label: 'GA — Gastrointestinal' },
  { code: 'HS', label: 'HS — Hormones/Synthetics/Modifiers' },
  { code: 'MS', label: 'MS — Musculoskeletal' },
  { code: 'RE', label: 'RE — Respiratory' },
  { code: 'BL', label: 'BL — Blood Products/Modifiers' },
  { code: 'DE', label: 'DE — Dermatological' },
  { code: 'OP', label: 'OP — Ophthalmic' },
  { code: 'AU', label: 'AU — Autonomic' },
  { code: 'AN', label: 'AN — Analgesics' },
  { code: 'OT', label: 'OT — Other' },
];

export default function Medications({ user }) {
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [medications, setMedications] = useState([]);
  const [showPrescribeForm, setShowPrescribeForm] = useState(false);
  const [editingMedicationId, setEditingMedicationId] = useState(null);
  const [discontinueWarningId, setDiscontinueWarningId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state for prescribe
  const [prescribeForm, setPrescribeForm] = useState({
    patientId: '',
    drugName: '',
    ndcCode: '',
    dosage: '',
    frequency: 'once daily',
    route: 'oral',
    startDate: '',
    endDate: '',
    notes: '',
    rxNumber: 'RX-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
    quantity: '',
    quantityUnit: 'tablets',
    refills: '0',
    deaSchedule: '',
    sig: '',
    pharmacy: '',
    daw: false,
    priorAuth: '',
    indication: '',
    prescriber: user?.display_name || user?.username || '',
    prescriptionWritten: '',
    rxNormCui: '',
    hcpcsCode: '',
    gpiCode: '',
    drugDbSource: '',
    atcCode: '',
    atcLevel1: '',
    vaClass: '',
    therapeuticCategory: '',
    controlledSubstance: false,
    brandGeneric: ''
  });

  // Drug interaction state
  const [drugInteractions, setDrugInteractions] = useState({});
  const [checkingDrug, setCheckingDrug] = useState(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    dosage: '',
    frequency: '',
    route: '',
    notes: '',
    sig: ''
  });

  // Discontinue reason state
  const [discontinueReason, setDiscontinueReason] = useState({});

  // Load patients on mount
  useEffect(() => {
    loadPatients();
  }, []);

  // Load medications when patient is selected
  useEffect(() => {
    if (selectedPatientId) {
      loadMedications();
    } else {
      setMedications([]);
    }
  }, [selectedPatientId]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const data = await window.zignagenixAPI.getPatients();
      setPatients(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError('Failed to load patients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMedications = async () => {
    try {
      setLoading(true);
      const data = await window.zignagenixAPI.getMedications(selectedPatientId);
      setMedications(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError('Failed to load medications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const checkDrugInteractions = async (patientId, drugName) => {
    if (!drugName || !drugName.trim()) return;

    try {
      setCheckingDrug(drugName);
      const interactions = await window.zignagenixAPI.checkDrugInteractions(patientId, drugName);
      setDrugInteractions(prev => ({
        ...prev,
        [drugName]: interactions
      }));
    } catch (err) {
      console.error('Failed to check drug interactions:', err);
    } finally {
      setCheckingDrug(null);
    }
  };

  const handlePrescribeFormChange = (field, value) => {
    setPrescribeForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDrugNameBlur = () => {
    if (prescribeForm.patientId && prescribeForm.drugName) {
      checkDrugInteractions(prescribeForm.patientId, prescribeForm.drugName);
    }
  };

  const handlePrescribeMedication = async () => {
    if (!prescribeForm.patientId || !prescribeForm.drugName || !prescribeForm.dosage || !prescribeForm.startDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await window.zignagenixAPI.prescribeMedication({
        patientId: prescribeForm.patientId,
        drugName: prescribeForm.drugName,
        ndcCode: prescribeForm.ndcCode,
        dosage: prescribeForm.dosage,
        frequency: prescribeForm.frequency,
        route: prescribeForm.route,
        startDate: prescribeForm.startDate,
        endDate: prescribeForm.endDate,
        notes: prescribeForm.notes,
        rxNumber: prescribeForm.rxNumber,
        quantity: prescribeForm.quantity,
        quantityUnit: prescribeForm.quantityUnit,
        refills: prescribeForm.refills,
        deaSchedule: prescribeForm.deaSchedule,
        sig: prescribeForm.sig,
        pharmacy: prescribeForm.pharmacy,
        daw: prescribeForm.daw,
        priorAuth: prescribeForm.priorAuth,
        indication: prescribeForm.indication,
        prescriber: prescribeForm.prescriber,
        prescriptionWritten: prescribeForm.prescriptionWritten,
        rxNormCui: prescribeForm.rxNormCui,
        hcpcsCode: prescribeForm.hcpcsCode,
        gpiCode: prescribeForm.gpiCode,
        drugDbSource: prescribeForm.drugDbSource,
        atcCode: prescribeForm.atcCode,
        atcLevel1: prescribeForm.atcLevel1,
        vaClass: prescribeForm.vaClass,
        therapeuticCategory: prescribeForm.therapeuticCategory,
        controlledSubstance: prescribeForm.controlledSubstance,
        brandGeneric: prescribeForm.brandGeneric
      });

      setPrescribeForm({
        patientId: selectedPatientId,
        drugName: '',
        ndcCode: '',
        dosage: '',
        frequency: 'once daily',
        route: 'oral',
        startDate: '',
        endDate: '',
        notes: '',
        rxNumber: 'RX-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
        quantity: '',
        quantityUnit: 'tablets',
        refills: '0',
        deaSchedule: '',
        sig: '',
        pharmacy: '',
        daw: false,
        priorAuth: '',
        indication: '',
        prescriber: user?.display_name || user?.username || '',
        prescriptionWritten: '',
        rxNormCui: '',
        hcpcsCode: '',
        gpiCode: '',
        drugDbSource: '',
        atcCode: '',
        atcLevel1: '',
        vaClass: '',
        therapeuticCategory: '',
        controlledSubstance: false,
        brandGeneric: ''
      });
      setShowPrescribeForm(false);
      setDrugInteractions({});
      await loadMedications();
      setError(null);
    } catch (err) {
      setError('Failed to prescribe medication');
      console.error(err);
    }
  };

  const handleEditMedication = (medication) => {
    setEditingMedicationId(medication.id);
    setEditForm({
      dosage: medication.dosage,
      frequency: medication.frequency,
      route: medication.route,
      notes: medication.notes || '',
      sig: medication.sig || ''
    });
  };

  const handleSaveEdit = async (medicationId) => {
    try {
      await window.zignagenixAPI.updateMedication(medicationId, {
        dosage: editForm.dosage,
        frequency: editForm.frequency,
        route: editForm.route,
        notes: editForm.notes,
        sig: editForm.sig
      });
      setEditingMedicationId(null);
      await loadMedications();
      setError(null);
    } catch (err) {
      setError('Failed to update medication');
      console.error(err);
    }
  };

  const handleDiscontinueMedication = async (medicationId) => {
    const reason = discontinueReason[medicationId] || '';
    if (!reason.trim()) {
      setError('Please provide a reason for discontinuing');
      return;
    }

    try {
      await window.zignagenixAPI.discontinueMedication(medicationId, reason);
      setDiscontinueWarningId(null);
      setDiscontinueReason(prev => ({
        ...prev,
        [medicationId]: ''
      }));
      await loadMedications();
      setError(null);
    } catch (err) {
      setError('Failed to discontinue medication');
      console.error(err);
    }
  };

  const hasCreatePermission = user?.permissions?.includes('medications:create');
  const hasUpdatePermission = user?.permissions?.includes('medications:update');
  const hasDeletePermission = user?.permissions?.includes('medications:delete');

  const getInteractionSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'minor':
        return '#FCD34D';
      case 'moderate':
        return '#F26522';
      case 'major':
        return '#DC3545';
      case 'contraindicated':
        return '#7F1D1D';
      default:
        return '#E5E7EB';
    }
  };

  const getInteractionSeverityLabel = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'minor':
        return 'Minor';
      case 'moderate':
        return 'Moderate';
      case 'major':
        return 'Major';
      case 'contraindicated':
        return 'Contraindicated';
      default:
        return severity || 'Unknown';
    }
  };

  if (loading && patients.length === 0) {
    return (
      <div className="page">
        <h1 className="page-title"><PillBottle size={22} style={{ marginRight: 8, color: '#2E8B98' }} /> Medications</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header-row">
        <h1 className="page-title"><PillBottle size={22} style={{ marginRight: 8, color: '#2E8B98' }} /> Medications</h1>
        <PrintButton pageTitle="Medications" />
      </div>

      {error && (
        <div className="alert alert--error">
          <AlertTriangle size={20} style={{ marginRight: '8px' }} />
          {error}
        </div>
      )}

      <div className="filter-bar">
        <PatientDropdown
          patients={patients}
          value={selectedPatientId}
          onChange={setSelectedPatientId}
          placeholder="Select a patient..."
          idPrefix="meds-filter"
        />

        {hasCreatePermission && selectedPatientId && (
          <button
            className="btn-primary"
            onClick={() => {
              setShowPrescribeForm(true);
              setPrescribeForm(prev => ({
                ...prev,
                patientId: selectedPatientId
              }));
            }}
          >
            <Plus size={18} style={{ marginRight: '6px' }} />
            Prescribe New
          </button>
        )}
      </div>

      {showPrescribeForm && (
        <div className="form-section">
          <h2 className="form-section__title">
            Prescribe New Medication
          </h2>

          <div className="patient-form-grid">
            <div className="form-group">
              <label className="form-label">Patient *</label>
              <PatientDropdown
                patients={patients}
                value={prescribeForm.patientId}
                onChange={val => handlePrescribeFormChange('patientId', val)}
                placeholder="Select patient"
                idPrefix="meds-form"
              />
            </div>

            {/* Row 1: Drug Name*, RxNorm CUI, DEA Schedule */}
            <div className="form-group">
              <label className="form-label">Drug Name *</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.drugName}
                onChange={(e) => handlePrescribeFormChange('drugName', e.target.value)}
                onBlur={handleDrugNameBlur}
                placeholder="e.g., Lisinopril"
              />
              {checkingDrug === prescribeForm.drugName && (
                <p className="form-helper-text">Checking interactions...</p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">RxNorm CUI</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.rxNormCui}
                onChange={(e) => handlePrescribeFormChange('rxNormCui', e.target.value)}
                placeholder="e.g., 197361"
              />
            </div>

            <div className="form-group">
              <label className="form-label">DEA Schedule</label>
              <select
                className="form-input"
                value={prescribeForm.deaSchedule}
                onChange={(e) => handlePrescribeFormChange('deaSchedule', e.target.value)}
              >
                <option value="">Non-scheduled</option>
                <option value="II">Schedule II</option>
                <option value="III">Schedule III</option>
                <option value="IV">Schedule IV</option>
                <option value="V">Schedule V</option>
              </select>
            </div>

            {/* Row 2: Dosage*, Quantity, Quantity Unit */}
            <div className="form-group">
              <label className="form-label">Dosage *</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.dosage}
                onChange={(e) => handlePrescribeFormChange('dosage', e.target.value)}
                placeholder="e.g., 10mg"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input
                type="number"
                className="form-input"
                value={prescribeForm.quantity}
                onChange={(e) => handlePrescribeFormChange('quantity', e.target.value)}
                placeholder="e.g., 30"
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Qty Unit</label>
              <select
                className="form-input"
                value={prescribeForm.quantityUnit}
                onChange={(e) => handlePrescribeFormChange('quantityUnit', e.target.value)}
              >
                <option value="tablets">Tablets</option>
                <option value="capsules">Capsules</option>
                <option value="mL">mL</option>
                <option value="patches">Patches</option>
                <option value="inhalers">Inhalers</option>
                <option value="doses">Doses</option>
                <option value="grams">Grams</option>
                <option value="units">Units</option>
              </select>
            </div>

            {/* Row 3: Frequency*, Route*, Refills */}
            <div className="form-group">
              <label className="form-label">Frequency *</label>
              <select
                className="form-input"
                value={prescribeForm.frequency}
                onChange={(e) => handlePrescribeFormChange('frequency', e.target.value)}
              >
                <option value="once daily">Once daily</option>
                <option value="twice daily">Twice daily</option>
                <option value="three times daily">Three times daily</option>
                <option value="four times daily">Four times daily</option>
                <option value="every 4 hours">Every 4 hours</option>
                <option value="every 6 hours">Every 6 hours</option>
                <option value="every 8 hours">Every 8 hours</option>
                <option value="as needed">As needed</option>
                <option value="at bedtime">At bedtime</option>
                <option value="with meals">With meals</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Route *</label>
              <select
                className="form-input"
                value={prescribeForm.route}
                onChange={(e) => handlePrescribeFormChange('route', e.target.value)}
              >
                <option value="oral">Oral</option>
                <option value="sublingual">Sublingual</option>
                <option value="topical">Topical</option>
                <option value="injection">Injection</option>
                <option value="inhalation">Inhalation</option>
                <option value="rectal">Rectal</option>
                <option value="transdermal">Transdermal</option>
                <option value="ophthalmic">Ophthalmic</option>
                <option value="nasal">Nasal</option>
                <option value="IV">IV</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Refills</label>
              <select
                className="form-input"
                value={prescribeForm.refills}
                onChange={(e) => handlePrescribeFormChange('refills', e.target.value)}
              >
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
              </select>
            </div>

            {/* Row 3.5: Coding Standards Section Header */}
            <div style={{ gridColumn: '1 / -1', marginTop: '12px', marginBottom: '8px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#2E8B98', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Coding Standards
              </h3>
            </div>

            {/* Row 4: NDC Code, HCPCS Code, GPI Code */}
            <div className="form-group">
              <label className="form-label">NDC Code</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.ndcCode}
                onChange={(e) => handlePrescribeFormChange('ndcCode', e.target.value)}
                placeholder="e.g., 0093-7517-01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">HCPCS Code</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.hcpcsCode}
                onChange={(e) => handlePrescribeFormChange('hcpcsCode', e.target.value)}
                placeholder="e.g., J9035"
              />
            </div>

            <div className="form-group">
              <label className="form-label">GPI Code</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.gpiCode}
                onChange={(e) => handlePrescribeFormChange('gpiCode', e.target.value)}
                placeholder="e.g., 36200020100310"
              />
            </div>

            {/* Row 5: Drug Database Source, NCPDP SCRIPT Badge */}
            <div className="form-group">
              <label className="form-label">Drug Database</label>
              <select
                className="form-input"
                value={prescribeForm.drugDbSource}
                onChange={(e) => handlePrescribeFormChange('drugDbSource', e.target.value)}
              >
                <option value="">Select database...</option>
                <option value="First Databank (FDB)">First Databank (FDB)</option>
                <option value="Medi-Span">Medi-Span</option>
                <option value="Multum">Multum</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
              <div style={{
                padding: '6px 12px',
                backgroundColor: '#E8F4F8',
                borderLeft: '4px solid #2E8B98',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                color: '#2E8B98',
                width: '100%'
              }}>
                NCPDP SCRIPT v2017071
              </div>
            </div>

            {/* FHIR R4 MedicationRequest — classification maps to:
                ATC: WHO Anatomical Therapeutic Chemical system for research grouping
                VA Class: Veterans Affairs National Drug File classification
                Used for population health analytics, drug utilization reviews, and formulary management */}
            <div style={{ gridColumn: '1 / -1', marginTop: '12px', marginBottom: '8px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#2E8B98', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Drug Classification & Research
              </h3>
            </div>

            {/* Row 6: ATC Level 1, ATC Code, VA Class */}
            <div className="form-group">
              <label className="form-label">ATC Level 1 Class</label>
              <select
                className="form-input"
                value={prescribeForm.atcLevel1}
                onChange={(e) => handlePrescribeFormChange('atcLevel1', e.target.value)}
              >
                <option value="">Select ATC class...</option>
                {ATC_LEVEL1_CLASSES.map(atc => (
                  <option key={atc.code} value={atc.code}>{atc.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">ATC Code</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.atcCode}
                onChange={(e) => handlePrescribeFormChange('atcCode', e.target.value)}
                placeholder="e.g., C09AA (ACE inhibitors)"
              />
            </div>

            <div className="form-group">
              <label className="form-label">VA Drug Class</label>
              <select
                className="form-input"
                value={prescribeForm.vaClass}
                onChange={(e) => handlePrescribeFormChange('vaClass', e.target.value)}
              >
                <option value="">Select VA class...</option>
                {VA_DRUG_CLASSES.map(va => (
                  <option key={va.code} value={va.code}>{va.label}</option>
                ))}
              </select>
            </div>

            {/* Row 7: Therapeutic Category, Brand/Generic, Controlled Substance */}
            <div className="form-group">
              <label className="form-label">Therapeutic Category</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.therapeuticCategory}
                onChange={(e) => handlePrescribeFormChange('therapeuticCategory', e.target.value)}
                placeholder="e.g., ACE Inhibitor, Statin, SSRI"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Brand/Generic</label>
              <select
                className="form-input"
                value={prescribeForm.brandGeneric}
                onChange={(e) => handlePrescribeFormChange('brandGeneric', e.target.value)}
              >
                <option value="">Select type...</option>
                <option value="Brand">Brand</option>
                <option value="Generic">Generic</option>
                <option value="Biosimilar">Biosimilar</option>
              </select>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="controlled-substance-checkbox"
                  checked={prescribeForm.controlledSubstance}
                  onChange={(e) => handlePrescribeFormChange('controlledSubstance', e.target.checked)}
                  style={{ width: 'auto', marginRight: '8px', cursor: 'pointer' }}
                />
                <label htmlFor="controlled-substance-checkbox" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
                  Controlled Substance
                </label>
              </div>
            </div>

            {/* Row 7: Start Date*, End Date, Pharmacy */}
            <div className="form-group">
              <label className="form-label">Start Date *</label>
              <input
                type="date"
                className="form-input"
                value={prescribeForm.startDate}
                onChange={(e) => handlePrescribeFormChange('startDate', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={prescribeForm.endDate}
                onChange={(e) => handlePrescribeFormChange('endDate', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Pharmacy</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.pharmacy}
                onChange={(e) => handlePrescribeFormChange('pharmacy', e.target.value)}
                placeholder="Pharmacy name"
              />
            </div>

            {/* Row 8: Prior Auth #, Indication/Diagnosis */}
            <div className="form-group">
              <label className="form-label">Prior Auth #</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.priorAuth}
                onChange={(e) => handlePrescribeFormChange('priorAuth', e.target.value)}
                placeholder="Prior authorization number"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Indication/Diagnosis</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.indication}
                onChange={(e) => handlePrescribeFormChange('indication', e.target.value)}
                placeholder="Clinical indication/diagnosis"
              />
            </div>
          </div>

          {/* Full-width: Sig / Directions */}
          <div className="form-group">
            <label className="form-label">Sig / Patient Directions</label>
            <textarea
              className="form-input"
              value={prescribeForm.sig}
              onChange={(e) => handlePrescribeFormChange('sig', e.target.value)}
              placeholder="e.g., Take 1 tablet by mouth daily"
              rows="2"
            />
          </div>

          {/* Full-width: Notes */}
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-input"
              value={prescribeForm.notes}
              onChange={(e) => handlePrescribeFormChange('notes', e.target.value)}
              placeholder="Additional notes..."
              rows="2"
            />
          </div>

          {/* Row 9: Prescriber, DAW */}
          <div className="patient-form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="form-group">
              <label className="form-label">Prescriber</label>
              <input
                type="text"
                className="form-input"
                value={prescribeForm.prescriber}
                onChange={(e) => handlePrescribeFormChange('prescriber', e.target.value)}
                placeholder="Prescribing provider name"
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="daw-checkbox"
                  checked={prescribeForm.daw}
                  onChange={(e) => handlePrescribeFormChange('daw', e.target.checked)}
                  style={{ width: 'auto', marginRight: '8px', cursor: 'pointer' }}
                />
                <label htmlFor="daw-checkbox" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
                  Dispense As Written (DAW)
                </label>
              </div>
            </div>
          </div>

          {/* Full-width: Prescription Written */}
          <div className="form-group">
            <label className="form-label">Prescription Written</label>
            <textarea
              className="form-input"
              value={prescribeForm.prescriptionWritten}
              onChange={(e) => handlePrescribeFormChange('prescriptionWritten', e.target.value)}
              placeholder="Full prescription as written, e.g.: Lisinopril 10mg tablets. Take 1 tablet by mouth once daily for hypertension. Qty: 30. Refills: 3. DAW: No."
              rows="3"
              style={{ fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          {drugInteractions[prescribeForm.drugName] && (
            <div className="alert--interaction" style={{
              backgroundColor: getInteractionSeverityColor(drugInteractions[prescribeForm.drugName].severity),
              borderColor: getInteractionSeverityColor(drugInteractions[prescribeForm.drugName].severity)
            }}>
              <div className="alert--interaction__content">
                {drugInteractions[prescribeForm.drugName].severity?.toLowerCase() === 'contraindicated' ? (
                  <Ban size={20} className="alert--interaction__icon" />
                ) : (
                  <AlertTriangle size={20} className="alert--interaction__icon" />
                )}
                <div>
                  <strong className="alert--interaction__title">
                    {getInteractionSeverityLabel(drugInteractions[prescribeForm.drugName].severity)} Drug Interaction
                  </strong>
                  <p className="alert--interaction__description">
                    {drugInteractions[prescribeForm.drugName].description || 'Potential interaction detected'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              className="btn-primary"
              onClick={handlePrescribeMedication}
            >
              <Check size={18} style={{ marginRight: '6px' }} />
              Prescribe
            </button>
            <button
              className="btn-ghost"
              onClick={() => {
                setShowPrescribeForm(false);
                setPrescribeForm({
                  patientId: selectedPatientId,
                  drugName: '',
                  ndcCode: '',
                  dosage: '',
                  frequency: 'once daily',
                  route: 'oral',
                  startDate: '',
                  endDate: '',
                  notes: '',
                  rxNumber: 'RX-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
                  quantity: '',
                  quantityUnit: 'tablets',
                  refills: '0',
                  deaSchedule: '',
                  sig: '',
                  pharmacy: '',
                  daw: false,
                  priorAuth: '',
                  indication: '',
                  prescriber: user?.display_name || user?.username || '',
                  prescriptionWritten: '',
                  rxNormCui: '',
                  hcpcsCode: '',
                  gpiCode: '',
                  drugDbSource: '',
                  atcCode: '',
                  atcLevel1: '',
                  vaClass: '',
                  therapeuticCategory: '',
                  controlledSubstance: false,
                  brandGeneric: ''
                });
                setDrugInteractions({});
              }}
            >
              <X size={18} style={{ marginRight: '6px' }} />
              Cancel
            </button>
          </div>
        </div>
      )}

      {selectedPatientId && (
        <div className="data-table-container">
          {medications.length === 0 ? (
            <p className="data-table-empty">
              No active medications for this patient
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rx #</th>
                  <th>Drug Name</th>
                  <th>RxNorm CUI</th>
                  <th>HCPCS Code</th>
                  <th>ATC Class</th>
                  <th>Dosage</th>
                  <th>Frequency</th>
                  <th>Route</th>
                  <th>Qty</th>
                  <th>Refills</th>
                  <th>Start Date</th>
                  <th>Prescriber</th>
                  <th>Rx Written</th>
                  <th>Notes</th>
                  <th className="table-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {medications.map(med => (
                  <tr key={med.id}>
                    <td>{med.rx_number || '-'}</td>
                    <td>
                      <div className="medication-name">
                        <Pill size={16} className="medication-name__icon" />
                        {med.drug_name}
                      </div>
                    </td>
                    <td>{med.rx_norm_cui || '-'}</td>
                    <td>{med.hcpcs_code || '-'}</td>
                    <td>{med.atc_level1 || '-'}</td>
                    <td>
                      {editingMedicationId === med.id ? (
                        <input
                          type="text"
                          className="form-input form-input--sm"
                          value={editForm.dosage}
                          onChange={(e) => setEditForm(prev => ({ ...prev, dosage: e.target.value }))}
                        />
                      ) : (
                        med.dosage
                      )}
                    </td>
                    <td>
                      {editingMedicationId === med.id ? (
                        <select
                          className="form-input form-input--sm"
                          value={editForm.frequency}
                          onChange={(e) => setEditForm(prev => ({ ...prev, frequency: e.target.value }))}
                        >
                          <option value="once daily">Once daily</option>
                          <option value="twice daily">Twice daily</option>
                          <option value="three times daily">Three times daily</option>
                          <option value="four times daily">Four times daily</option>
                          <option value="every 4 hours">Every 4 hours</option>
                          <option value="every 6 hours">Every 6 hours</option>
                          <option value="every 8 hours">Every 8 hours</option>
                          <option value="as needed">As needed</option>
                          <option value="at bedtime">At bedtime</option>
                          <option value="with meals">With meals</option>
                        </select>
                      ) : (
                        med.frequency
                      )}
                    </td>
                    <td>
                      {editingMedicationId === med.id ? (
                        <select
                          className="form-input form-input--sm"
                          value={editForm.route}
                          onChange={(e) => setEditForm(prev => ({ ...prev, route: e.target.value }))}
                        >
                          <option value="oral">Oral</option>
                          <option value="sublingual">Sublingual</option>
                          <option value="topical">Topical</option>
                          <option value="injection">Injection</option>
                          <option value="inhalation">Inhalation</option>
                          <option value="rectal">Rectal</option>
                          <option value="transdermal">Transdermal</option>
                          <option value="ophthalmic">Ophthalmic</option>
                          <option value="nasal">Nasal</option>
                          <option value="IV">IV</option>
                        </select>
                      ) : (
                        med.route
                      )}
                    </td>
                    <td>{med.quantity ? `${med.quantity} ${med.quantity_unit || ''}` : '-'}</td>
                    <td>{med.refills || '0'}</td>
                    <td>{med.start_date}</td>
                    <td>{med.prescriber_name || '-'}</td>
                    <td style={{ maxWidth: 200, whiteSpace: 'pre-wrap', fontSize: 12, lineHeight: 1.4 }}>
                      {med.prescription_written || '-'}
                    </td>
                    <td>
                      {editingMedicationId === med.id ? (
                        <textarea
                          className="form-input form-input--sm"
                          value={editForm.notes}
                          onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                        />
                      ) : (
                        med.notes || '-'
                      )}
                    </td>
                    <td className="table-center">
                      {editingMedicationId === med.id ? (
                        <div className="button-group">
                          <button
                            className="btn-icon btn-icon--success"
                            onClick={() => handleSaveEdit(med.id)}
                          >
                            <Save size={14} />
                          </button>
                          <button
                            className="btn-icon btn-icon--gray"
                            onClick={() => setEditingMedicationId(null)}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="button-group">
                          {hasUpdatePermission && (
                            <button
                              className="btn-icon btn-icon--primary"
                              onClick={() => handleEditMedication(med)}
                            >
                              <Edit3 size={14} />
                            </button>
                          )}
                          {hasDeletePermission && (
                            <button
                              className="btn-icon-danger"
                              onClick={() => setDiscontinueWarningId(med.id)}
                            >
                              <Ban size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {discontinueWarningId && (
        <div className="alert alert--danger">
          <div className="alert--danger__content">
            <AlertTriangle size={20} className="alert--danger__icon" />
            <div className="alert--danger__body">
              <p className="alert--danger__title">
                Discontinue Medication
              </p>
              <input
                type="text"
                className="form-input alert--danger__input"
                placeholder="Reason for discontinuing..."
                value={discontinueReason[discontinueWarningId] || ''}
                onChange={(e) => setDiscontinueReason(prev => ({
                  ...prev,
                  [discontinueWarningId]: e.target.value
                }))}
              />
              <div className="form-actions form-actions--inline">
                <button
                  className="btn-danger"
                  onClick={() => handleDiscontinueMedication(discontinueWarningId)}
                >
                  <Ban size={16} style={{ marginRight: '6px' }} />
                  Discontinue
                </button>
                <button
                  className="btn-ghost"
                  onClick={() => {
                    setDiscontinueWarningId(null);
                    setDiscontinueReason(prev => ({
                      ...prev,
                      [discontinueWarningId]: ''
                    }));
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
